import { alertController, modalController, popoverController, toastController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import {
  Constants,
  DateUtils,
  FeedItemDoc,
  FeedItemSubType,
  FeedItemType,
  FileDoc,
  FilePageDoc,
  FileType,
  PhotoAnalysis,
  PhotoDoc,
  PersonDoc,
  RfiDoc,
  RFISettingsDoc,
  RoleUtils,
  TaskDoc,
  TaskRelationship,
  WorkflowState,
} from 'common';
import { ChangeEvent, ConcourseDb, DbListener } from 'src/global/ConcourseDb';
import { FileService } from 'src/global/service/FileService';
import { ImageService } from 'src/global/service/ImageService';
import { PeopleService } from 'src/global/service/PeopleService';
import { RfiService } from 'src/RFIs/services/RfiService';
import { WorkflowUtils } from '../../global/utils/WorkflowUtils';
import { ProjectService } from 'src/projects/services/ProjectService';
import { ScheduleService } from 'src/global/service/ScheduleService';
import { Person, User } from '../../global/models';
import { SharedUtils } from '../../global/utils/SharedUtils';
import { PermissionsSet } from '../../global/models/User';
import { WorkflowHistoryModal } from '../../submittals/components/workflow-history-modal';
import { WorkflowStateModal } from '../../submittals/components/workflow-state-modal';
import { RfiCreateModal } from './rfi-create-modal';
import { FeedFilter, FeedService, peopleState, TimelineUtils } from '../../global';
import { RfiUtils, RfiWorkflowUtils } from '..';
import { Calendar } from '../../components/concourse-schedule/model/Calendar';

import { RequestAccess } from '../../components/concourse-login/share-link/request-access';
import { PersonInfoModal } from '../../components/concourse-person-selector/person-info-modal';
import { DocumentViewerModal } from '../../components/concourse-documents/document-viewer-modal';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { ConcourseMessageModal } from '../../components/common/concourse-message-modal';
import { CommentSubmit } from '../../global/models/CommentSubmit';

export interface GalleryData {
  relatedId: string;
  images: string[];
}

@Component({
  tag: 'concourse-rfi-detail',
  styleUrls: ['./concourse-rfi-detail.css', '../../global/styles/workflow-view.css'],
  //shadow: true,
})
export class ConcourseRfiDetail {
  static async present(projectId: string, rfiId: string, cssClass: string = 'modal-height-auto'): Promise<RfiDoc | null> {
    const modal = await modalController.create({
      component: 'concourse-rfi-detail',
      componentProps: { rfiId, projectId, isModal: true, showLogo: false },
      cssClass: cssClass,
      backdropDismiss: false,
    });

    return new Promise(async resolve => {
      modal.addEventListener('save', (e: CustomEvent<RfiDoc>) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  // -------------------------
  // PROPS, STATE & EVENTS
  // -------------------------

  @Prop() projectId: string;
  @Prop() rfiId: string;
  @Prop() isModal: boolean;
  @Prop() isSidebarView: boolean;

  @State() loading: boolean = false;
  @State() settings: RFISettingsDoc;
  @State() rfi: RfiDoc;
  @State() files: FileDoc[] = [];
  @State() feed: FeedItemDoc[];
  @State() createModalOpen: boolean;
  @State() detailsExpanded: boolean = false;
  @State() descriptionTextExpanded: boolean = false;
  @State() newComment: string = '';
  @State() scheduleTasks: TaskDoc[] = [];
  @State() today: Date = DateUtils.currentDate;
  @State() replayDate: Date;
  @State() activeFilter: FeedFilter = FeedFilter.ALL;
  @State() feedSelected: FeedItemDoc[] = [];
  @State() calendar: Calendar;

  @Event() rfiEdit;
  @Event() rfiUpdated;
  @Event({ bubbles: true }) inviteUser;
  @Event() dismiss: EventEmitter;
  @Event() closeClick?: EventEmitter<void>;

  // -------------------------
  // PRIVATE FIELDS
  // -------------------------

  private commentsRef: HTMLDivElement;
  private rfiPermissions: PermissionsSet;
  private filePages: Map<string, FilePageDoc[]> = new Map();

  private rfisDbListener: DbListener = {
    onComplete: (docs: RfiDoc[]) => {
      this.rfi = docs.find(doc => doc._id === this.rfiId && !doc.deleted_date);
    },
    onChange: (event: ChangeEvent) => {
      if (event.direction === 'pull') {
        this.rfi = event.docs.find(doc => doc._id === this.rfiId && !doc.deleted_date);
      }
    },
    onError: (error: Error) => {
      console.error('RFIs DB error:', error);
    },
  };

  private feedDbListener: DbListener = {
    onComplete: (docs: FeedItemDoc[]) => {
      this.feed = this.filterFeedForRfi(docs);
    },
    onChange: (event: ChangeEvent) => {
      this.feed = this.filterFeedForRfi(event.docs);
    },
    onError: (error: Error) => {
      console.error('Feed DB error:', error);
    },
  };

  private filesDbListener: DbListener = {
    onComplete: (docs: FileDoc[]) => {
      this.files = docs;
    },
    onChange: (event: ChangeEvent) => {
      this.files = event.docs;
    },
    onError: (error: Error) => {
      console.error('Files DB error:', error);
    },
  };

  private filterFeedForRfi(docs: FeedItemDoc[]): FeedItemDoc[] {
    return docs.filter(item => item.type === FeedItemType.RFI && item.related_id === this.rfiId).sort(FeedService.sortByDateAsc);
  }

  // -------------------------
  // GETTERS
  // -------------------------

  get user(): User {
    return globalThis.user;
  }

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  get isMobileOrSidebar(): boolean {
    return this.isMobile || this.isSidebarView;
  }

  get canRead(): boolean {
    //return this.rfiPermissions.canRead || this.user.getSingleRfiPermissions(this.projectId, this.rfiId).canRead;
    return RoleUtils.canRead(this.user.roles, this.projectId, 'rfis', this.rfiId);
  }

  get canWrite(): boolean {
    return this.rfiPermissions.canWrite || this.user.getSingleRfiPermissions(this.projectId, this.rfiId).canWrite;
  }
  get acceptedAnswers(): string[] {
    return this.rfi.accepted_answers || [];
  }

  get activeBubbleIndex(): number {
    return this.rfi.status_history.length;
  }

  get writeRoles() {
    return [`p-${this.projectId}-rfis-${this.rfiId}-write`, `p-${this.projectId}-rfis-write`, `p-${this.projectId}-write`];
  }

  get readRoles() {
    return [...this.writeRoles, `p-${this.projectId}-rfis-${this.rfiId}`, `p-${this.projectId}-rfis`, `p-${this.projectId}`];
  }

  get inputClass() {
    return this.canWrite ? 'pseudo-input-editable' : 'pseudo-input';
  }

  get people(): PersonDoc[] {
    return peopleState.people;
  }

  get canAcceptComment(): boolean {
    const currentState = RfiWorkflowUtils.getCurrentState(this.rfi);
    return currentState?.id === Constants.SUBMITTAL_APPROVAL_STATUS_ID && currentState.dueBy.includes(this.user.id);
  }

  // -------------------------
  // LIFECYCLE
  // -------------------------

  async componentWillLoad() {
    this.loading = true;
    this.today = this.replayDate = DateUtils.currentDate;
    this.rfiPermissions = this.user.getRfiPermissions(this.projectId);

    if (!this.canRead) {
      this.loading = false;
      return;
    }

    try {
      this.settings = await RfiService.getRfiSettings(this.projectId);
      await this.loadRfiData();
      await this.loadFilesData();
      await this.loadFeedData();
      await this.loadCalendar();

      if (this.user.getSchedulePermissions(this.projectId).canRead && this.rfi?.successors?.length) {
        this.loadScheduleTasks();
      }

      if (this.isMobile && this.isSidebarView && !this.isModal) {
        this.showFullScreen();
      }
    } finally {
      this.loading = false;
    }
  }

  async disconnectedCallback() {
    const rfisDb = RfiService.getRfisDb(this.projectId);
    rfisDb.removeListeners(this.rfisDbListener);
    const filesDb = FileService.getFilesDb(this.projectId);
    filesDb.removeListeners(this.filesDbListener);
    const feedDb = FeedService.getDb(this.projectId);
    feedDb.removeListeners(this.feedDbListener);

    const tasksDb = ScheduleService.getTasksDb(this.projectId);
    tasksDb.removeListeners(this.tasksDbListener);
  }

  private async loadRfiData() {
    const rfisDb = RfiService.getRfisDb(this.projectId);
    rfisDb.addListeners(this.rfisDbListener);
    await rfisDb.syncRemote();
  }

  private async loadFeedData() {
    const feedDb = FeedService.getDb(this.projectId);
    feedDb.addListeners(this.feedDbListener);
    await feedDb.syncRemote();
  }

  private async loadFilesData() {
    const filesDb = FileService.getFilesDb(this.projectId);
    filesDb.addListeners(this.filesDbListener);
    await filesDb.syncRemote();
  }

  private async loadCalendar() {
    const projectDoc = await ProjectService.getProjectDoc(this.projectId);
    this.calendar = await ScheduleService.getCalendarOrDefault(this.projectId, projectDoc.default_calendar_unique_id);
  }

  // -------------------------
  // DATA / SAVE
  // -------------------------

  async loadScheduleTasks() {
    const tasksDb = ScheduleService.getTasksDb(this.projectId);
    tasksDb.addListeners(this.tasksDbListener);
    await tasksDb.syncRemote();
  }

  private tasksDbListener: DbListener = {
    onComplete: (docs: TaskDoc[]) => {
      this.scheduleTasks = docs;
    },
    onChange: (event: ChangeEvent) => {
      if (event.direction === 'pull') {
        this.scheduleTasks = event.docs;
      }
    },
    onError: (error: Error) => {
      console.error('Tasks DB error:', error);
    },
  };

  async setAcceptedAnswer(answerId: string, text: string) {
    this.rfi = RfiUtils.setAcceptedAnswer(this.rfi, answerId, this.user, text);
    this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
  }

  onRemoveTaskClick(relationship: TaskRelationship) {
    return this.removeTask(relationship.task_unique_id);
  }

  async removeTask(taskId: number) {
    this.rfi = RfiUtils.removeTask(this.rfi, taskId);
    this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
  }

  async removeDistributionPerson(person: PersonDoc) {
    this.rfi = RfiUtils.removeDistribution(this.rfi, person._id, this.user.id);
    this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
  }

  async onPersonRequested(name: string) {
    if (!this.user.canAdministerPeople(this.projectId)) return;
    const permissions = this.user.getRfiPermissions(this.projectId);
    this.inviteUser.emit({ name, role: permissions.readRole, hash: document.location.hash });
  }

  // -------------------------
  // NAVIGATION & MODALS
  // -------------------------

  handleHeaderDismiss() {
    this.closeClick?.emit();
    if (this.isModal) {
      this.dismiss.emit();
    } else {
      window.location.hash = `/project/${this.projectId}/rfis`;
    }
  }

  async showEditModal() {
    if (!this.canWrite) {
      return this.showRequestAccessModal();
    }

    if (!this.rfiPermissions.canWrite) {
      return RequestAccess.presentModal(this.projectId, 'Edit Rfis', this.rfiPermissions.writeRole);
    }

    const updatedRfi = await RfiCreateModal.present(this.projectId, { ...this.rfi });
    if (updatedRfi) {
      if (this.rfi.subject != updatedRfi.subject) {
        this.addFeedItem(FeedItemSubType.SUBJECT, updatedRfi.subject);
      }
      if (this.rfi.description != updatedRfi.description) {
        this.addFeedItem(FeedItemSubType.DESCRIPTION, updatedRfi.description);
      }
      if (this.rfi.priority != updatedRfi.priority) {
        this.addFeedItem(FeedItemSubType.PRIORITY, updatedRfi.description);
      }
      if (this.rfi.cost_code != updatedRfi.cost_code) {
        // updatedRfi.addHistory(`changed the cost code from ${this.rfiDetail.doc.costCode} to ${updatedRfi.doc.costCode}`, this.user.id);
      }
      if (this.rfi.cost_impact != updatedRfi.cost_impact) {
        //updatedRfi.addHistory(`changed the cost impact from ${this.rfiDetail.doc.costImpact} to ${updatedRfi.doc.costImpact}`, this.user.id);
      }
      if (this.rfi.schedule_impact != updatedRfi.schedule_impact) {
        //updatedRfi.addHistory(`changed the schedule impact from ${this.rfiDetail.doc.scheduleImpact} to ${updatedRfi.doc.scheduleImpact}`, this.user.id);
      }
      this.rfi = await RfiService.saveRfi(this.projectId, updatedRfi);
    }
  }

  showRequestAccessModal(role: string = null, callback: Function = null) {
    RequestAccess.presentModal(this.projectId, 'Edit: ' + this.rfi.subject, role || this.rfiPermissions.writeRole, callback);
  }

  async createRfi() {
    if (!this.canWrite) {
      this.showRequestAccessModal();
    } else {
      this.showCreateModal();
    }
  }

  async showCreateModal() {
    this.createModalOpen = true;
    const result = await RfiCreateModal.present(this.projectId, null);
    this.createModalOpen = false;
    if (result?.num) {
      document.location.hash = `/project/${this.projectId}/rfi/${result.num}`;
    } else {
      document.location.hash = `/project/${this.projectId}/rfis/`;
    }
  }

  toggleDescriptionText() {
    this.descriptionTextExpanded = !this.descriptionTextExpanded;
  }

  setFilter(filter: FeedFilter) {
    this.activeFilter = filter;
  }

  /** this.settings only loads when we have the permissions */
  hasField(name: string): boolean {
    return !!this.rfi[name] || (this.settings && this.settings.fields.includes(name));
  }

  formatText(text: string) {
    return PersonInfoModal.stringToRichText(text, this.people, 'showPersonInfo');
  }

  showPersonInfo(personId: string) {
    const person = PeopleService.findPerson(this.people, personId);
    PersonInfoModal.presentModal(person);
  }

  async showFileViewer(author: Person, file: FileDoc) {
    DocumentViewerModal.present(this.projectId, file);
  }

  navigateToScrapbook(scrapbook: FileDoc) {
    window.location.href = `#/project/${this.projectId}/scrapbook/${scrapbook._id}`;
  }

  onNavigateToRfis() {
    if (!this.rfiPermissions.canRead) {
      return this.showRequestAccessModal();
    }
    document.location.hash = `/project/${this.projectId}/rfis`;
  }

  onAddCommentClick() {
    if (!this.user.doc.email) {
      this.showRequestAccessModal(this.rfiPermissions.readRole, () => this.addComment());
    } else {
      this.addComment();
    }
  }

  onCommentsLoadError(error: any) {
    console.error('Failed to load comments:', error);
  }

  onPeopleLoadError(error: any) {
    if (error.message == 'Failed to fetch' && this.people && this.people.length) return;
    alertController
      .create({
        header: 'Problem loading people',
        message: error.message == 'Failed to fetch' ? 'You appear to be offline' : error.message,
      })
      .then(alert => alert.present());
  }

  async onActivityLoadComplete(activities: FeedItemDoc[]) {
    console.log('onActivityLoadComplete', activities);
  }

  // -------------------------
  // WORKFLOW / STATES
  // -------------------------

  async moveToPrevState() {
    if (!this.rfiPermissions.canWrite) {
      return RequestAccess.presentModal(this.projectId, 'Edit Rfis', this.rfiPermissions.writeRole);
    }
    const idx = RfiWorkflowUtils.getLastCompletedStateIdx(this.rfi);
    const prevState = this.rfi.states[idx - 1];
    const prevStateId = prevState ? prevState.id : null;
    const prevActor = this.people.find(p => p._id === this.rfi.status_history[idx - 1]?.user_id);

    const message = await ConcourseMessageModal.present(
      `Return RFI to ${prevActor?.first_name || 'previous user'}`,
      'Please provide a message explaining why this RFI is being returned.',
      'Return',
    );

    if (message) {
      try {
        this.rfi = await RfiService.completeState(this.projectId, this.rfi, prevStateId, [], message);

        toastController
          .create({
            message: `The RFI has been returned to ${prevActor?.first_name || 'previous user'}`,
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
      } catch (error) {
        console.error('Error returning RFI:', error);
      }
    }
  }

  async moveToNextState() {
    if (!this.rfiPermissions.canWrite) {
      return RequestAccess.presentModal(this.projectId, 'Edit Rfis', this.rfiPermissions.writeRole);
    }
    const currentState = RfiWorkflowUtils.getCurrentState(this.rfi);
    const currentStateIndex = RfiWorkflowUtils.getStateIndex(this.rfi);

    // Check if next state has a dueBy

    const { canChange, responsibleNames } = WorkflowUtils.validateIfCanChangeWorkflowState(currentState, this.user.id, this.people);
    if (!canChange) {
      const alert = await alertController.create({
        header: 'You are not responsible for this state',
        message: `Only ${responsibleNames} may ${currentState.action} this submittal`,
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    // Check if we need accepted answers for the approval state
    if (!WorkflowUtils.validateIfHasAcceptedAnswers(currentState, this.rfi.accepted_answers) && currentState.id === Constants.SUBMITTAL_APPROVAL_STATUS_ID) {
      const alert = await alertController.create({
        header: 'No answers selected',
        message: 'Please approve one of the responses below',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    try {
      console.log('next step', { rfi: this.rfi, currentStateIndex });
      const nextStepHasAssignees = WorkflowUtils.validateNextStateHasAssignees(this.rfi.states, currentStateIndex);
      if (!nextStepHasAssignees) {
        const people = await ConcoursePersonSelector.present({
          projectId: this.projectId,
          feature: 'rfis',
          featureId: this.rfi._id,
          peopleToOmitIds: currentState.dueBy,
          multiSelect: true,
          needsWrite: false,
          modalTitle: 'Choose who will be responsible for this state',
        });
        if (people && people.length > 0) {
          this.rfi.states[currentStateIndex + 1].dueBy = people.map(p => p._id);
          this.rfi = await RfiService.completeState(this.projectId, this.rfi, currentState.id);
        }
      } else {
        this.rfi = await RfiService.completeState(this.projectId, this.rfi, currentState.id);
      }

      toastController
        .create({
          message: `The RFI has been ${currentState.pastTense}`,
          duration: 2000,
          position: 'bottom',
        })
        .then(toast => toast.present());
    } catch (error) {
      toastController
        .create({
          message: `Could Not Complete State`,
          duration: 2000,
          position: 'bottom',
          color: 'danger',
        })
        .then(toast => toast.present());
    }
  }

  handleTextAreaChange(textarea: HTMLTextAreaElement) {
    this.toggleButtonsVisible(textarea.value && textarea.value.length > 1);
  }

  toggleButtonsVisible(haveText: boolean) {
    const cameraButton = document.getElementById('camera-button');
    if (cameraButton) {
      cameraButton.style.display = haveText ? 'none' : 'block';
      const commentButton = document.getElementById('comment-button');
      commentButton.style.display = haveText ? 'block' : 'none';
    }
  }

  async onBarClick(index: number) {
    console.log('Showing pre-pending item:', index);
    const statusHistory = this.rfi.status_history || [];
    const state: WorkflowState = {
      id: null,
      dueBy: [],
      dueDays: 0,
      action: '',
      pastTense: '',
    };
    const updated = await WorkflowStateModal.present(this.projectId, state, 'rfis', this.rfi._id, false);
    if (updated && (updated as WorkflowState)) {
      const stateIndex = statusHistory.length + index - 1;
      this.rfi.states.splice(stateIndex, 0, { ...(updated as WorkflowState), id: ConcourseDb.makeShortDocId() });
      this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
    }
  }

  async onBubbleClick(indexes: number[]) {
    const statusHistory = this.rfi.status_history || [];
    if (indexes.length == 1 && indexes[0] >= statusHistory.length) {
      const currentIdx = statusHistory.length;
      const stateIndex = indexes[0] - statusHistory.length + currentIdx;
      const state = this.rfi.states[stateIndex];
      const canDelete = RfiUtils.DEFAULT_RFI_WORKFLOW.states.find(s => s.id === state.id) === undefined;
      const updated = await WorkflowStateModal.present(this.projectId, state, 'rfis', this.rfi._id, canDelete);
      if (updated && (updated as WorkflowState)) {
        this.rfi.states[stateIndex] = updated as WorkflowState;
        this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
        toastController
          .create({
            message: 'Submittal state updated successfully',
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
      }
      if (updated && typeof updated === 'string') {
        console.warn('Submittal state to be deleted:', updated);
        this.rfi.states = this.rfi.states.filter((s, idx) => idx !== stateIndex);
        toastController
          .create({
            message: 'Submittal state deleted successfully',
            duration: 2000,
            position: 'bottom',
          })
          .then(toast => toast.present());
        console.log('Submittal state deleted:', updated);
      }
    } else {
      const history = statusHistory.filter((_, idx) => indexes.includes(idx));
      const selected = await WorkflowHistoryModal.presentModal(this.people, this.rfi.states, history);
      if (selected) {
        console.log('Selected event:', selected);
        const stateIndex = this.rfi.states.findIndex(s => s.id === selected.id);
        const lastCompleted = this.rfi.states[stateIndex - 1];
        //this.rfiDetail.moveToState(DateUtils.currentDate, this.user, lastCompleted);
      }
    }
  }

  // -------------------------
  // COMMENTS
  // -------------------------

  async onCommentSubmit(detail: CommentSubmit) {
    const { message, files, people } = detail;
    for (const person of people) {
      await this.onPersonReferenced(person);
    }
    if (!files.length) {
      if (message) await this.addComment(message);
      else this.newComment = '';
      return;
    }
    for (const file of files) {
      const sub = file.type === FileType.PHOTO ? FeedItemSubType.PHOTO : FeedItemSubType.FILE;
      await this.addFeedItem(sub, file._id, message || null);
      if (file.type === FileType.PHOTO) {
        const analysis: PhotoAnalysis = await ImageService.analyzePhoto(this.projectId, file.s3_key);
        await FileService.savePhoto(this.projectId, { ...(file as PhotoDoc), description: analysis.description, tags: analysis.tags });
      }
    }
    this.newComment = '';
    this.activeFilter = FeedFilter.ALL;
    SharedUtils.scrollToBottom(this.commentsRef, 200);
  }

  // -------------------------
  // FEED ITEMS
  // -------------------------

  async addFeedItem(sub: FeedItemSubType, subId: string = null, text: string = null) {
    const newItem = FeedService.newFeedItem(this.user.id, FeedItemType.RFI, this.rfiId, sub, subId, text);
    try {
      await FeedService.storeFeedItem(this.projectId, newItem);
      SharedUtils.scrollToBottom(this.commentsRef, 200);
    } catch (error) {
      console.error('Error adding feed item:', error);
    }
  }

  async addComment(message?: string): Promise<void> {
    const text = message ?? this.newComment;
    if (!text || !this.canWrite) return;
    await this.addFeedItem(FeedItemSubType.COMMENT, null, text);
    this.newComment = '';
    this.activeFilter = FeedFilter.ALL;
  }

  async onPersonReferenced(person: PersonDoc) {
    const distribution = this.rfi.distribution || [];
    if (!distribution.includes(person._id)) {
      distribution.push(person._id);
      this.rfi.distribution = distribution;
      await RfiService.saveRfi(this.projectId, this.rfi);
    }
  }

  // -------------------------
  // RENDER
  // -------------------------

  headerButtons() {
    const statusHistory = this.rfi.status_history || [];
    const currentState = RfiWorkflowUtils.getCurrentState(this.rfi);
    const { stateComplete, returnToState, isResponsible } = WorkflowUtils.getWorkflowRenderState(this.rfi.states, statusHistory, currentState, this.user?.id);

    return [
      this.canWrite && (
        <ion-button fill="clear" shape="round" class="" onClick={() => this.showEditModal()}>
          <ion-icon slot="icon-only" src="/assets/icon/pencil.svg"></ion-icon>
        </ion-button>
      ),
      this.canWrite && WorkflowUtils.validateIfCanReturnWorkflow(this.rfi, currentState, this.user.id, returnToState) && (
        <ion-button color="primary" fill="outline" shape="round" class="ion-margin-end" onClick={() => this.moveToPrevState()}>
          <ion-label class="ion-hide-md-up">Return</ion-label>
          <ion-label class="ion-hide-md-down">Return to {PeopleService.getNameFromId(this.people, stateComplete?.user_id)}</ion-label>
        </ion-button>
      ),
      isResponsible && (
        <ion-button data-testid="rfi-next-action" color="primary" fill="solid" shape="round" class="ion-margin-end" onClick={() => this.moveToNextState()}>
          {currentState.action}
        </ion-button>
      ),
    ];
  }

  async showFullScreen() {
    await ConcourseRfiDetail.present(this.projectId, this.rfiId, 'modal-full-view');
    if (this.isSidebarView) {
      this.closeClick?.emit();
    }
  }

  render() {
    if (!this.canRead) {
      return (
        <Fragment>
          {this.isSidebarView ? (
            <sidepanel-header number={this.rfi?.num ?? '—'} subject={this.rfi?.subject ?? 'RFI'} onShowFullscreen={() => undefined} onCloseClick={() => this.closeClick?.emit()} />
          ) : (
            <concourse-header
              // backCallback={() => this.handleHeaderDismiss()}
              projectId={this.projectId}
              pageTitle="RFI"
              showLogo={false}
            />
          )}
          <ion-content class="rfi-detail__no-access-content" style={{ '--background': this.isSidebarView ? 'var(--background-color-step-50)' : 'var(--ion-background-color)' }}>
            <div class="rfi-detail__no-access ion-padding">
              <p class="rfi-detail__no-access-title">You don't have access to this RFI</p>
              <p class="rfi-detail__no-access-body">Your project role does not allow viewing RFI details.</p>
            </div>
          </ion-content>
        </Fragment>
      );
    }

    if (this.loading) {
      return (
        <div class="rfi-detail__loading ion-padding">
          <ion-spinner name="crescent" />
        </div>
      );
    }

    if (!this.rfi) {
      return (
        <Fragment>
          {this.isSidebarView ? <sidepanel-header number="—" subject="RFI" onShowFullscreen={() => undefined} onCloseClick={() => this.closeClick?.emit()} /> : null}
          <ion-content class="rfi-detail__no-access-content" style={{ '--background': this.isSidebarView ? 'var(--background-color-step-50)' : 'var(--ion-background-color)' }}>
            <div class="rfi-detail__no-access ion-padding rfi-detail__no-access--muted">
              <p class="rfi-detail__no-access-body">This RFI could not be loaded. It may have been removed, or a sync error occurred.</p>
            </div>
          </ion-content>
        </Fragment>
      );
    }
    const statusHistory = this.rfi.status_history || [];
    const currentState = RfiWorkflowUtils.getCurrentState(this.rfi);
    const { stateComplete, returnToState, isResponsible } = WorkflowUtils.getWorkflowRenderState(this.rfi.states, statusHistory, currentState, this.user?.id);
    return (
      <Fragment>
        {this.isSidebarView ? (
          <sidepanel-header number={this.rfi.num} subject={this.rfi.subject} onShowFullscreen={() => this.showFullScreen()} onCloseClick={() => this.closeClick?.emit()} />
        ) : (
          <concourse-header
            showBack={true}
            onBack={() => this.handleHeaderDismiss()}
            projectId={this.projectId}
            pageTitle={`RFI #${this.rfi.num}`}
            shareRoles={this.isMobile ? null : [`p-${this.projectId}-rfis-${this.rfiId}`]}
            showLogo={false}
          >
            {this.headerButtons()}
          </concourse-header>
        )}
        <ion-content style={{ '--background': this.isSidebarView ? 'var(--background-color-step-50)' : 'var(--ion-background-color)' }}>
          <ion-grid class="ion-no-padding ion-display-flex ion-flex-column" style={{ height: this.isSidebarView ? 'calc(100% - 29px)' : '100%' }}>
            {/* <ion-row class="ion-padding-vertical">
              <ion-col class="cc-padding-horizontal-sm-up">
                {this.rfi && this.calendar && (
                  <concourse-timeline
                    bubbles={TimelineUtils.getTimelineBubbles(this.rfi.status_history, this.rfi.states, this.people, this.calendar)}
                    activeIndex={this.activeBubbleIndex}
                    dueDate={DateUtils.fromISO(this.rfi.due_date)}
                    onBarClick={e => this.onBarClick(e.detail)}
                    onBubbleClick={e => this.onBubbleClick(e.detail)}
                  />
                )}
              </ion-col>
            </ion-row> */}
            <ion-row class="flex-grow workflow-view__content-container">
              <ion-col size-md={this.isMobileOrSidebar ? '12' : '8'} style={{ height: '100%' }}>
                {this.detailsExpanded ? (
                  <rfi-details-card
                    rfi={this.rfi}
                    people={this.people}
                    scheduleTasks={this.scheduleTasks}
                    descriptionTextExpanded={this.descriptionTextExpanded}
                    projectId={this.projectId}
                    hasField={(name: string) => this.hasField(name)}
                    removeTaskClick={(relationship: TaskRelationship) => this.onRemoveTaskClick(relationship)}
                    toggleDescriptionText={() => this.toggleDescriptionText()}
                  />
                ) : (
                  <div class="ion-display-flex ion-flex-column workflow-view__left-column">
                    {this.rfi && this.calendar && (
                      <div class="ion-padding-horizontal workflow-view__timeline-container">
                        <concourse-timeline
                          bubbles={TimelineUtils.getTimelineBubbles(this.rfi.status_history, this.rfi.states, this.people, this.calendar)}
                          activeIndex={this.activeBubbleIndex}
                          dueDate={DateUtils.fromISO(this.rfi.due_date)}
                          onBarClick={e => this.onBarClick(e.detail)}
                          onBubbleClick={e => this.onBubbleClick(e.detail)}
                        />
                      </div>
                    )}
                    <rfi-comments-card
                      class="ion-flex-1 workflow-view__comments-container"
                      projectId={this.projectId}
                      rfiId={this.rfiId}
                      people={this.people}
                      files={this.files}
                      feed={this.feed}
                      canWrite={this.canWrite}
                      isMobile={this.isMobileOrSidebar}
                      newComment={this.newComment}
                      activeFilter={this.activeFilter}
                      acceptedAnswers={this.acceptedAnswers}
                      readRoles={this.readRoles}
                      onSetFilter={(e: CustomEvent<FeedFilter>) => this.setFilter(e.detail)}
                      onCommentSubmit={(e: CustomEvent<CommentSubmit>) => this.onCommentSubmit(e.detail)}
                      onNewCommentChange={(e: CustomEvent<string>) => (this.newComment = e.detail)}
                      onFeedSelectedChange={(e: CustomEvent<FeedItemDoc[]>) => (this.feedSelected = e.detail)}
                      onSetCommentsRef={(e: CustomEvent<HTMLDivElement>) => (this.commentsRef = e.detail)}
                      onSetAcceptedAnswerClick={(e: CustomEvent<{ answerId: string; text: string }>) => this.setAcceptedAnswer(e.detail.answerId, e.detail.text)}
                      isApprovingStep={this.canAcceptComment}
                    />
                  </div>
                )}
              </ion-col>
              {!this.isMobileOrSidebar && (
                <ion-col size-md="4" class="workflow-view-detail-info-container vertical-scroll cc-scrollbars ion-hide-md-down">
                  <rfi-details-card
                    rfi={this.rfi}
                    people={this.people}
                    scheduleTasks={this.scheduleTasks}
                    descriptionTextExpanded={this.descriptionTextExpanded}
                    projectId={this.projectId}
                    hasField={(name: string) => this.hasField(name)}
                    removeTaskClick={(relationship: TaskRelationship) => this.onRemoveTaskClick(relationship)}
                    toggleDescriptionText={() => this.toggleDescriptionText()}
                  />
                </ion-col>
              )}
            </ion-row>
            {this.isMobileOrSidebar && <workflow-view-footer detailsExpanded={this.detailsExpanded} onDetailsExpandedChange={e => (this.detailsExpanded = e.detail)} />}
          </ion-grid>
        </ion-content>
      </Fragment>
    );
  }
}
